import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useEffect,
  useRef,
} from 'react';
import type {
  CatalogSnapshot,
  ContinueWatchingEntry,
  FavoriteEntry,
  LiveChannel,
  NavId,
  PlayableItem,
  PlayerSettings,
  RecentEntry,
  SeriesItem,
  StreamServer,
  VodItem,
  XtreamUserInfoResponse,
} from '../types';
import { fetchM3uFromUrl, parseM3uText } from '../adapters/m3uParser';
import {
  loadXtreamCatalog,
  loadXtreamSeriesDetail,
  playBaseFromLogin,
  xtreamLogin,
} from '../adapters/xtreamAdapter';
import {
  loadServers,
  saveServers,
  loadFavorites,
  saveFavorites,
  loadRecents,
  saveRecents,
  loadContinueWatching,
  saveContinueWatching,
  loadPlayerSettings,
  savePlayerSettings,
  loadLastPlayed,
  saveLastPlayed,
  catalogConnectionFingerprint,
  loadCatalogCache,
  saveCatalogCache,
  removeCatalogCache,
} from '../storage/streamPlayerStorage';
import { duplicateStreamServer as cloneServer } from '../storage/migrateServers';
import { normalizeServerUrl } from '../utils/serverFormUtils';
import { getConnectionCountryBlockReason } from '../utils/connectionCountryCheck';

export type BrowseTab = 'live' | 'movies' | 'series' | 'favorites';

interface State {
  nav: NavId;
  browseTab: BrowseTab;
  activeSourceId: string;
  servers: StreamServer[];
  catalog: CatalogSnapshot | null;
  xtreamMeta: Record<string, { login: XtreamUserInfoResponse; user: string; pass: string }>;
  loading: boolean;
  loadLabel: string | null;
  error: string | null;
  searchQuery: string;
  selectedLiveCategory: string;
  selectedVodCategory: string;
  selectedSeriesCategory: string;
  playback: PlayableItem | null;
  miniPlayer: boolean;
  favorites: FavoriteEntry[];
  recents: RecentEntry[];
  continueWatching: ContinueWatchingEntry[];
  settings: PlayerSettings;
  lastPlayed: RecentEntry | null;
  detailVod: VodItem | null;
  detailSeries: SeriesItem | null;
}

type Action =
  | { type: 'SET_NAV'; nav: NavId }
  | { type: 'SET_BROWSE_TAB'; tab: BrowseTab }
  | { type: 'SET_SERVERS'; servers: StreamServer[] }
  | { type: 'SET_CATALOG'; catalog: CatalogSnapshot | null; keepSelections?: boolean }
  | { type: 'SET_SOURCE'; sourceId: string }
  | { type: 'DROP_XTREAM_META'; serverId: string }
  | { type: 'SET_LOADING'; loading: boolean; label?: string | null }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SEARCH'; q: string }
  | { type: 'SET_LIVE_CAT'; id: string }
  | { type: 'SET_VOD_CAT'; id: string }
  | { type: 'SET_SERIES_CAT'; id: string }
  | { type: 'PLAY'; item: PlayableItem | null }
  | { type: 'SET_MINI'; mini: boolean }
  | { type: 'SET_FAVORITES'; items: FavoriteEntry[] }
  | { type: 'SET_RECENTS'; items: RecentEntry[] }
  | { type: 'SET_CONTINUE'; items: ContinueWatchingEntry[] }
  | { type: 'SET_SETTINGS'; s: PlayerSettings }
  | { type: 'SET_LAST_PLAYED'; r: RecentEntry | null }
  | { type: 'SET_DETAIL_VOD'; v: VodItem | null }
  | { type: 'SET_DETAIL_SERIES'; s: SeriesItem | null }
  | { type: 'SET_XTREAM_META'; serverId: string; login: XtreamUserInfoResponse; user: string; pass: string }
  | { type: 'CLEAR_XTREAM_META' };

function initialSettings(): PlayerSettings {
  return loadPlayerSettings();
}

const initialState: State = {
  nav: 'browse',
  browseTab: 'live',
  activeSourceId: '',
  servers: [],
  catalog: null,
  xtreamMeta: {},
  loading: false,
  loadLabel: null,
  error: null,
  searchQuery: '',
  selectedLiveCategory: 'all',
  selectedVodCategory: 'all',
  selectedSeriesCategory: 'all',
  playback: null,
  miniPlayer: false,
  favorites: [],
  recents: [],
  continueWatching: [],
  settings: initialSettings(),
  lastPlayed: null,
  detailVod: null,
  detailSeries: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_NAV':
      return { ...state, nav: action.nav };
    case 'SET_BROWSE_TAB':
      return { ...state, browseTab: action.tab };
    case 'SET_SERVERS':
      return { ...state, servers: action.servers };
    case 'SET_CATALOG': {
      const keep = action.keepSelections === true;
      return {
        ...state,
        catalog: action.catalog,
        ...(!keep
          ? {
              selectedLiveCategory: 'all' as const,
              selectedVodCategory: 'all' as const,
              selectedSeriesCategory: 'all' as const,
            }
          : {}),
      };
    }
    case 'SET_SOURCE':
      return {
        ...state,
        activeSourceId: action.sourceId,
      };
    case 'DROP_XTREAM_META': {
      const { [action.serverId]: _, ...rest } = state.xtreamMeta;
      return { ...state, xtreamMeta: rest };
    }
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.loading,
        loadLabel: action.label ?? null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.q };
    case 'SET_LIVE_CAT':
      return { ...state, selectedLiveCategory: action.id };
    case 'SET_VOD_CAT':
      return { ...state, selectedVodCategory: action.id };
    case 'SET_SERIES_CAT':
      return { ...state, selectedSeriesCategory: action.id };
    case 'PLAY':
      return { ...state, playback: action.item };
    case 'SET_MINI':
      return { ...state, miniPlayer: action.mini };
    case 'SET_FAVORITES':
      return { ...state, favorites: action.items };
    case 'SET_RECENTS':
      return { ...state, recents: action.items };
    case 'SET_CONTINUE':
      return { ...state, continueWatching: action.items };
    case 'SET_SETTINGS':
      return { ...state, settings: action.s };
    case 'SET_LAST_PLAYED':
      return { ...state, lastPlayed: action.r };
    case 'SET_DETAIL_VOD':
      return { ...state, detailVod: action.v };
    case 'SET_DETAIL_SERIES':
      return { ...state, detailSeries: action.s };
    case 'SET_XTREAM_META':
      return {
        ...state,
        xtreamMeta: {
          ...state.xtreamMeta,
          [action.serverId]: { login: action.login, user: action.user, pass: action.pass },
        },
      };
    case 'CLEAR_XTREAM_META':
      return { ...state, xtreamMeta: {} };
    default:
      return state;
  }
}

function favoriteKey(sourceId: string, kind: FavoriteEntry['kind'], refId: string): string {
  return `${sourceId}:${kind}:${refId}`;
}

function persistServerPatch(id: string, patch: Partial<StreamServer>): StreamServer[] {
  const now = new Date().toISOString();
  const list = loadServers().map((s) =>
    s.id === id ? { ...s, ...patch, updatedAt: now } : s,
  );
  saveServers(list);
  return list;
}

interface CtxValue extends State {
  /** Stops an in-flight M3U URL playlist fetch (user cancel). */
  cancelCatalogLoad: () => void;
  loadServerCatalog: (server: StreamServer) => Promise<void>;
  saveStreamServer: (server: StreamServer, options?: { connect?: boolean }) => Promise<void>;
  updateStreamServer: (server: StreamServer) => void;
  removeServer: (id: string) => void;
  duplicateServer: (id: string) => void;
  openNav: (n: NavId) => void;
  setBrowseTab: (t: BrowseTab) => void;
  setSearchQuery: (q: string) => void;
  setLiveCategory: (id: string) => void;
  setVodCategory: (id: string) => void;
  setSeriesCategory: (id: string) => void;
  playItem: (item: PlayableItem) => void;
  stopPlayback: () => void;
  setMiniPlayer: (v: boolean) => void;
  toggleFavoriteFromLive: (ch: LiveChannel) => void;
  toggleFavoriteFromVod: (v: VodItem) => void;
  toggleFavoriteFromSeries: (s: SeriesItem) => void;
  toggleFavoriteFromEpisode: (series: SeriesItem, episodeId: string, title: string, streamUrl: string) => void;
  isFavorite: (kind: FavoriteEntry['kind'], refId: string) => boolean;
  updateSettings: (patch: Partial<PlayerSettings>) => void;
  recordProgress: (item: PlayableItem, positionSeconds: number, durationSeconds: number) => void;
  openVodDetail: (v: VodItem) => void;
  closeVodDetail: () => void;
  openSeriesDetail: (s: SeriesItem) => void;
  closeSeriesDetail: () => void;
  ensureSeriesLoaded: (s: SeriesItem) => Promise<void>;
  getMergedCatalog: () => CatalogSnapshot | null;
  activeServer: StreamServer | null;
  clearError: () => void;
}

const StreamPlayerContext = createContext<CtxValue | null>(null);

export function StreamPlayerProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  /** Throttle SET_CONTINUE — frequent dispatches re-render resizable panels and break element fullscreen. */
  const continueWatchUiRef = useRef<{ at: number; key: string }>({ at: 0, key: '' });
  /** In-flight M3U URL load — abort() stops fetch + clears loading. */
  const catalogLoadAbortRef = useRef<AbortController | null>(null);
  /** Ignore stale geo checks when the user switches servers quickly. */
  const countryCheckGenRef = useRef(0);

  const cancelCatalogLoad = useCallback(() => {
    catalogLoadAbortRef.current?.abort();
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', error: null });
  }, []);

  const loadServerCatalog = useCallback(async (server: StreamServer) => {
    const kind = server.sourceKind ?? 'xtream';
    const fingerprint = catalogConnectionFingerprint(server);
    const cached = loadCatalogCache(server.id, fingerprint);
    const hadCache = Boolean(cached);

    const checkGen = (countryCheckGenRef.current += 1);
    const needsCountry =
      Boolean(server.restrictConnectionToCountry) && Boolean(server.connectionCountryCode?.trim());

    if (needsCountry) {
      dispatch({ type: 'SET_LOADING', loading: true, label: 'Verifying location…' });
      dispatch({ type: 'SET_ERROR', error: null });
    }

    let blockReason: string | null = null;
    try {
      blockReason = await getConnectionCountryBlockReason(server);
    } catch {
      blockReason =
        'Country verification failed unexpectedly. Connection blocked for this restricted server.';
    }

    if (countryCheckGenRef.current !== checkGen) {
      dispatch({ type: 'SET_LOADING', loading: false, label: null });
      return;
    }

    if (blockReason) {
      dispatch({ type: 'SET_ERROR', error: blockReason });
      const listErr = persistServerPatch(server.id, { lastError: blockReason });
      dispatch({ type: 'SET_SERVERS', servers: listErr });
      dispatch({ type: 'SET_LOADING', loading: false, label: null });
      return;
    }

    if (cached) {
      dispatch({ type: 'SET_ERROR', error: null });
      dispatch({ type: 'SET_CATALOG', catalog: cached });
      dispatch({ type: 'SET_SOURCE', sourceId: server.id });
      const nextSettings = { ...loadPlayerSettings(), activeServerId: server.id };
      savePlayerSettings(nextSettings);
      dispatch({ type: 'SET_SETTINGS', s: nextSettings });
      const connectedAt = new Date().toISOString();
      const listOk = persistServerPatch(server.id, {
        lastConnectedAt: connectedAt,
        lastError: null,
      });
      dispatch({ type: 'SET_SERVERS', servers: listOk });
      dispatch({ type: 'SET_NAV', nav: 'browse' });
      dispatch({ type: 'SET_BROWSE_TAB', tab: 'live' });
      dispatch({ type: 'SET_LOADING', loading: false, label: null });
      return;
    }

    catalogLoadAbortRef.current?.abort();
    catalogLoadAbortRef.current = null;

    dispatch({ type: 'SET_LOADING', loading: true, label: 'Connecting…' });
    dispatch({ type: 'SET_ERROR', error: null });

    let m3uAbort: AbortController | null = null;
    try {
      let catalog: CatalogSnapshot;

      if (kind === 'm3u_url') {
        m3uAbort = new AbortController();
        catalogLoadAbortRef.current = m3uAbort;
        dispatch({ type: 'SET_LOADING', loading: true, label: 'Fetching playlist…' });
        const text = await fetchM3uFromUrl(server.server, { signal: m3uAbort.signal });
        dispatch({ type: 'SET_LOADING', loading: true, label: 'Parsing playlist…' });
        catalog = parseM3uText(text, {
          sourceId: server.id,
          defaultName: server.name,
        });
      } else if (kind === 'm3u_file' && server.m3uFileContent) {
        if (!hadCache) {
          dispatch({ type: 'SET_LOADING', loading: true, label: 'Parsing playlist…' });
        }
        catalog = parseM3uText(server.m3uFileContent, {
          sourceId: server.id,
          defaultName: server.name,
        });
      } else {
        const base = normalizeServerUrl(server.server);
        const user = server.username.trim();
        const pass = server.password;
        if (!base || !user || !pass) {
          throw new Error('Missing server URL, username, or password.');
        }
        if (!hadCache) {
          dispatch({ type: 'SET_LOADING', loading: true, label: 'Connecting…' });
        }
        const login = await xtreamLogin(base, user, pass);
        dispatch({
          type: 'SET_XTREAM_META',
          serverId: server.id,
          login,
          user,
          pass,
        });
        if (!hadCache) {
          dispatch({ type: 'SET_LOADING', loading: true, label: 'Loading library…' });
        }
        catalog = await loadXtreamCatalog(base, user, pass, server.id);
      }

      dispatch({ type: 'SET_CATALOG', catalog });
      dispatch({ type: 'SET_SOURCE', sourceId: server.id });
      saveCatalogCache(server.id, fingerprint, catalog);

      const nextSettings = { ...loadPlayerSettings(), activeServerId: server.id };
      savePlayerSettings(nextSettings);
      dispatch({ type: 'SET_SETTINGS', s: nextSettings });

      const connectedAtOk = new Date().toISOString();
      const listOk = persistServerPatch(server.id, {
        lastConnectedAt: connectedAtOk,
        lastError: null,
      });
      dispatch({ type: 'SET_SERVERS', servers: listOk });
      dispatch({ type: 'SET_NAV', nav: 'browse' });
      dispatch({ type: 'SET_BROWSE_TAB', tab: 'live' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Load failed';
      if (!hadCache) {
        dispatch({ type: 'SET_ERROR', error: msg });
        if (msg !== 'Loading cancelled.') {
          const listErr = persistServerPatch(server.id, { lastError: msg });
          dispatch({ type: 'SET_SERVERS', servers: listErr });
        }
      }
    } finally {
      if (m3uAbort && catalogLoadAbortRef.current === m3uAbort) {
        catalogLoadAbortRef.current = null;
      }
      dispatch({ type: 'SET_LOADING', loading: false, label: null });
    }
  }, []);

  useEffect(() => {
    dispatch({ type: 'SET_SERVERS', servers: loadServers() });
    dispatch({ type: 'SET_FAVORITES', items: loadFavorites() });
    dispatch({ type: 'SET_RECENTS', items: loadRecents() });
    dispatch({ type: 'SET_CONTINUE', items: loadContinueWatching() });
    dispatch({ type: 'SET_LAST_PLAYED', r: loadLastPlayed() });
    const s = loadPlayerSettings();
    dispatch({ type: 'SET_SETTINGS', s });

    if (s.activeServerId === null) {
      dispatch({ type: 'SET_CATALOG', catalog: null });
      dispatch({ type: 'SET_SOURCE', sourceId: '' });
    } else {
      const srv = loadServers().find((x) => x.id === s.activeServerId);
      if (srv) {
        void loadServerCatalog(srv);
      } else {
        dispatch({ type: 'SET_CATALOG', catalog: null });
        dispatch({ type: 'SET_SOURCE', sourceId: '' });
        const fix = { ...s, activeServerId: null };
        savePlayerSettings(fix);
        dispatch({ type: 'SET_SETTINGS', s: fix });
      }
    }
  }, [loadServerCatalog]);

  const saveStreamServer = useCallback(
    async (server: StreamServer, options?: { connect?: boolean }) => {
      const list = loadServers();
      const idx = list.findIndex((x) => x.id === server.id);
      if (idx >= 0) list[idx] = server;
      else list.push(server);
      saveServers(list);
      dispatch({ type: 'SET_SERVERS', servers: list });
      if (options?.connect) {
        await loadServerCatalog(server);
      }
    },
    [loadServerCatalog],
  );

  const updateStreamServer = useCallback((server: StreamServer) => {
    const list = loadServers().map((x) => (x.id === server.id ? server : x));
    saveServers(list);
    dispatch({ type: 'SET_SERVERS', servers: list });
  }, []);

  const removeServer = useCallback(
    (id: string) => {
      removeCatalogCache(id);
      const list = loadServers().filter((s) => s.id !== id);
      saveServers(list);
      dispatch({ type: 'SET_SERVERS', servers: list });
      dispatch({ type: 'DROP_XTREAM_META', serverId: id });
      if (state.activeSourceId === id || loadPlayerSettings().activeServerId === id) {
        countryCheckGenRef.current += 1;
        dispatch({ type: 'SET_ERROR', error: null });
        dispatch({ type: 'SET_CATALOG', catalog: null });
        dispatch({ type: 'SET_SOURCE', sourceId: '' });
        const next = { ...loadPlayerSettings(), activeServerId: null };
        savePlayerSettings(next);
        dispatch({ type: 'SET_SETTINGS', s: next });
      }
    },
    [state.activeSourceId],
  );

  const duplicateServer = useCallback((id: string) => {
    const s = loadServers().find((x) => x.id === id);
    if (!s) return;
    const copy = cloneServer(s);
    const list = [...loadServers(), copy];
    saveServers(list);
    dispatch({ type: 'SET_SERVERS', servers: list });
  }, []);

  const openNav = useCallback((n: NavId) => {
    dispatch({ type: 'SET_NAV', nav: n });
  }, []);

  const setBrowseTab = useCallback((t: BrowseTab) => {
    dispatch({ type: 'SET_BROWSE_TAB', tab: t });
    dispatch({ type: 'SET_NAV', nav: 'browse' });
  }, []);

  const setSearchQuery = useCallback((q: string) => {
    dispatch({ type: 'SET_SEARCH', q });
  }, []);

  const setLiveCategory = useCallback((id: string) => {
    dispatch({ type: 'SET_LIVE_CAT', id });
  }, []);

  const setVodCategory = useCallback((id: string) => {
    dispatch({ type: 'SET_VOD_CAT', id });
  }, []);

  const setSeriesCategory = useCallback((id: string) => {
    dispatch({ type: 'SET_SERIES_CAT', id });
  }, []);

  const pushRecent = useCallback(
    (item: PlayableItem, position?: number, duration?: number) => {
      const sourceId = state.activeSourceId;
      const key = `${sourceId}:${item.kind}:${item.id}`;
      const entry: RecentEntry = {
        key,
        refId: item.id,
        title: item.title,
        kind: item.kind,
        poster: item.poster,
        streamUrl: item.streamUrl,
        sourceId,
        playedAt: Date.now(),
        positionSeconds: position,
        durationSeconds: duration,
      };
      const prev = loadRecents().filter((r) => r.key !== key);
      prev.unshift(entry);
      const trimmed = prev.slice(0, 50);
      saveRecents(trimmed);
      dispatch({ type: 'SET_RECENTS', items: trimmed });
      saveLastPlayed(entry);
      dispatch({ type: 'SET_LAST_PLAYED', r: entry });
    },
    [state.activeSourceId],
  );

  const playItem = useCallback(
    (item: PlayableItem) => {
      dispatch({ type: 'PLAY', item });
      pushRecent(item, 0, item.durationSeconds);
    },
    [pushRecent],
  );

  const stopPlayback = useCallback(() => {
    dispatch({ type: 'PLAY', item: null });
  }, []);

  const setMiniPlayer = useCallback((v: boolean) => {
    dispatch({ type: 'SET_MINI', mini: v });
  }, []);

  const isFavorite = useCallback(
    (kind: FavoriteEntry['kind'], refId: string) => {
      const sid = state.activeSourceId;
      const k = favoriteKey(sid, kind, refId);
      return state.favorites.some((f) => f.key === k);
    },
    [state.favorites, state.activeSourceId],
  );

  const writeFavorites = useCallback((items: FavoriteEntry[]) => {
    saveFavorites(items);
    dispatch({ type: 'SET_FAVORITES', items });
  }, []);

  const toggleFavoriteFromLive = useCallback(
    (ch: LiveChannel) => {
      const sid = state.activeSourceId;
      const k = favoriteKey(sid, 'live', ch.id);
      const exists = state.favorites.some((f) => f.key === k);
      const next = exists
        ? state.favorites.filter((f) => f.key !== k)
        : [
            ...state.favorites,
            {
              key: k,
              title: ch.name,
              kind: 'live' as const,
              poster: ch.logo,
              sourceId: sid,
              refId: ch.id,
              streamUrl: ch.streamUrl,
            },
          ];
      writeFavorites(next);
    },
    [state.favorites, state.activeSourceId, writeFavorites],
  );

  const toggleFavoriteFromVod = useCallback(
    (v: VodItem) => {
      const sid = state.activeSourceId;
      const k = favoriteKey(sid, 'vod', v.id);
      const exists = state.favorites.some((f) => f.key === k);
      const next = exists
        ? state.favorites.filter((f) => f.key !== k)
        : [
            ...state.favorites,
            {
              key: k,
              title: v.name,
              kind: 'vod' as const,
              poster: v.poster,
              sourceId: sid,
              refId: v.id,
              streamUrl: v.streamUrl,
            },
          ];
      writeFavorites(next);
    },
    [state.favorites, state.activeSourceId, writeFavorites],
  );

  const toggleFavoriteFromSeries = useCallback(
    (s: SeriesItem) => {
      const sid = state.activeSourceId;
      const k = favoriteKey(sid, 'series', s.id);
      const exists = state.favorites.some((f) => f.key === k);
      const next = exists
        ? state.favorites.filter((f) => f.key !== k)
        : [
            ...state.favorites,
            {
              key: k,
              title: s.name,
              kind: 'series' as const,
              poster: s.poster,
              sourceId: sid,
              refId: s.id,
            },
          ];
      writeFavorites(next);
    },
    [state.favorites, state.activeSourceId, writeFavorites],
  );

  const toggleFavoriteFromEpisode = useCallback(
    (series: SeriesItem, episodeId: string, title: string, streamUrl: string) => {
      const sid = state.activeSourceId;
      const k = favoriteKey(sid, 'episode', episodeId);
      const exists = state.favorites.some((f) => f.key === k);
      const next = exists
        ? state.favorites.filter((f) => f.key !== k)
        : [
            ...state.favorites,
            {
              key: k,
              title: `${series.name} — ${title}`,
              kind: 'episode' as const,
              poster: series.poster,
              sourceId: sid,
              refId: episodeId,
              seriesId: series.id,
              streamUrl,
            },
          ];
      writeFavorites(next);
    },
    [state.favorites, state.activeSourceId, writeFavorites],
  );

  const updateSettings = useCallback((patch: Partial<PlayerSettings>) => {
    const next = { ...state.settings, ...patch };
    savePlayerSettings(next);
    dispatch({ type: 'SET_SETTINGS', s: next });
  }, [state.settings]);

  const recordProgress = useCallback(
    (item: PlayableItem, positionSeconds: number, durationSeconds: number) => {
      if (!durationSeconds || item.isLive) return;
      const sourceId = state.activeSourceId;
      const key = `${sourceId}:${item.kind}:${item.id}`;
      const list = loadContinueWatching().filter((c) => c.key !== key);
      list.unshift({
        key,
        refId: item.id,
        title: item.title,
        poster: item.poster,
        streamUrl: item.streamUrl,
        sourceId,
        positionSeconds,
        durationSeconds,
        updatedAt: Date.now(),
      });
      const trimmed = list.slice(0, 30);
      saveContinueWatching(trimmed);

      const r = continueWatchUiRef.current;
      const now = Date.now();
      const nearEnd = positionSeconds >= durationSeconds * 0.97;
      const newTitle = r.key !== key;
      if (newTitle || nearEnd || now - r.at >= 3000) {
        r.key = key;
        r.at = now;
        dispatch({ type: 'SET_CONTINUE', items: trimmed });
      }
    },
    [state.activeSourceId],
  );

  const openVodDetail = useCallback((v: VodItem) => {
    dispatch({ type: 'SET_DETAIL_VOD', v });
  }, []);

  const closeVodDetail = useCallback(() => {
    dispatch({ type: 'SET_DETAIL_VOD', v: null });
  }, []);

  const openSeriesDetail = useCallback((s: SeriesItem) => {
    dispatch({ type: 'SET_DETAIL_SERIES', s });
  }, []);

  const closeSeriesDetail = useCallback(() => {
    dispatch({ type: 'SET_DETAIL_SERIES', s: null });
  }, []);

  const ensureSeriesLoaded = useCallback(
    async (s: SeriesItem) => {
      if (!state.catalog) return;
      if (state.catalog.seriesDetails[s.id]) return;
      const server = state.servers.find((x) => x.id === state.activeSourceId);
      const kind = server?.sourceKind ?? 'xtream';
      if (!server || kind !== 'xtream') {
        if (s.streamUrl) {
          const detail = {
            series: s,
            seasons: [
              {
                seasonNumber: 1,
                episodes: [
                  {
                    id: `${s.id}-m3u`,
                    seasonNumber: 1,
                    episodeNumber: 1,
                    title: s.name,
                    streamUrl: s.streamUrl,
                  },
                ],
              },
            ],
          };
          dispatch({
            type: 'SET_CATALOG',
            keepSelections: true,
            catalog: {
              ...state.catalog,
              seriesDetails: { ...state.catalog.seriesDetails, [s.id]: detail },
            },
          });
        }
        return;
      }
      const base = normalizeServerUrl(server.server);
      const meta = state.xtreamMeta[server.id];
      const pass = meta?.pass ?? server.password;
      const user = meta?.user ?? server.username;
      if (!pass) return;
      const login = meta?.login ?? (await xtreamLogin(base, user, pass));
      const playBase = playBaseFromLogin(base, login);
      const detail = await loadXtreamSeriesDetail(base, user, pass, s, playBase);
      dispatch({
        type: 'SET_CATALOG',
        keepSelections: true,
        catalog: {
          ...state.catalog,
          seriesDetails: { ...state.catalog.seriesDetails, [s.id]: detail },
        },
      });
    },
    [state.catalog, state.servers, state.activeSourceId, state.xtreamMeta],
  );

  const getMergedCatalog = useCallback((): CatalogSnapshot | null => state.catalog, [state.catalog]);

  const activeServer = useMemo(
    () => state.servers.find((x) => x.id === state.activeSourceId) ?? null,
    [state.servers, state.activeSourceId],
  );

  const value = useMemo<CtxValue>(
    () => ({
      ...state,
      cancelCatalogLoad,
      loadServerCatalog,
      saveStreamServer,
      updateStreamServer,
      removeServer,
      duplicateServer,
      openNav,
      setBrowseTab,
      setSearchQuery,
      setLiveCategory,
      setVodCategory,
      setSeriesCategory,
      playItem,
      stopPlayback,
      setMiniPlayer,
      toggleFavoriteFromLive,
      toggleFavoriteFromVod,
      toggleFavoriteFromSeries,
      toggleFavoriteFromEpisode,
      isFavorite,
      updateSettings,
      recordProgress,
      openVodDetail,
      closeVodDetail,
      openSeriesDetail,
      closeSeriesDetail,
      ensureSeriesLoaded,
      getMergedCatalog,
      activeServer,
      clearError,
    }),
    [
      state,
      clearError,
      cancelCatalogLoad,
      loadServerCatalog,
      saveStreamServer,
      updateStreamServer,
      removeServer,
      duplicateServer,
      openNav,
      setBrowseTab,
      setSearchQuery,
      setLiveCategory,
      setVodCategory,
      setSeriesCategory,
      playItem,
      stopPlayback,
      setMiniPlayer,
      toggleFavoriteFromLive,
      toggleFavoriteFromVod,
      toggleFavoriteFromSeries,
      toggleFavoriteFromEpisode,
      isFavorite,
      updateSettings,
      recordProgress,
      openVodDetail,
      closeVodDetail,
      openSeriesDetail,
      closeSeriesDetail,
      ensureSeriesLoaded,
      getMergedCatalog,
      activeServer,
    ],
  );

  return (
    <StreamPlayerContext.Provider value={value}>{children}</StreamPlayerContext.Provider>
  );
}

export function useStreamPlayer(): CtxValue {
  const ctx = useContext(StreamPlayerContext);
  if (!ctx) throw new Error('useStreamPlayer outside provider');
  return ctx;
}
